import { defineStore } from 'pinia';
import { cloneDeep, get, each, find, map } from 'lodash';
import Vue from 'vue';
import uniqid from 'uniqid';

import PaymentMethodApi from '@/extensions/shop/store/api/paymentMethod';
import MemberTagApi from '@/extensions/restrictedArea/store/api/memberTag';
import CustomerGroupApi from '@/extensions/shop/store/api/customerGroup';

import { set } from '@/helpers';

const defaultForm = {
    name: '',
    description: '',
    additional_info: '',
    service: '',
    digital: undefined,
    instant: undefined,
    environment: undefined,
    shop_flag: undefined,
    point_of_sale_flag: undefined,
    position: undefined,
    paymentMethodRules: [],
    active: true
};

const paymentMethodPriceRules = {
    prices: []
};

const newRuleObject = {
    name: '',
    type: 'price',
    min_sum: undefined,
    max_sum: undefined,
    memberTags: [],
    customerGroups: [],
    countries: []
};

const paymentMethodRule = {
    name: 'required',
    type: 'required',
    min_sum: 'required',
    max_sum: '',
    countries: 'required',
    memberTags: 'required',
};

export const useShopPaymentStore = defineStore('shopPayment', {
    state: () => ({
        loading: true,
        itemsPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        rules: {
            name: 'required',
            service: 'required',
            paymentMethodRules: []
        },
        options: {
            customerGroup: {
                loading: false,
                items: [],
            },
            memberTag: {
                loading: false,
                items: []
            },
        },
        form: cloneDeep(defaultForm),
        paymentMethodPriceRules: cloneDeep(paymentMethodPriceRules)
    }),

    getters: {},

    actions: {
        resetForm() {
            Vue.set(this, 'form', cloneDeep(defaultForm));
        },

        addRule(name, type = 'price') {
            let newRule = cloneDeep(newRuleObject);
            newRule.id = uniqid('new:');
            newRule.name = name;
            newRule.type = type;

            let copy = cloneDeep(this.form.paymentMethodRules);
            copy.push(newRule);

            Vue.set(this.form, 'paymentMethodRules', copy);
            this.rules.paymentMethodRules.push(paymentMethodRule);
        },

        removeRule(index) {
            Vue.delete(this.form.paymentMethodRules, index);
            Vue.delete(this.rules.paymentMethodRules, index);
        },

        updateRuleValidation({ index, path, value }) {
            const ruleValue = get(this.form.paymentMethodRules, index);
            switch (path) {
                case 'min_sum':
                case 'max_sum':
                case 'min_points':
                case 'max_points':
                    let key = 'sum';
                    if (['min_points', 'max_points'].includes(path)) key = 'points';

                    if (ruleValue[`max_${key}`] == null) {
                        const min_rule = `required`;
                        Vue.set(this.rules.paymentMethodRules[index], `min_${key}`, min_rule);
                        const max_rule = undefined;
                        Vue.set(this.rules.paymentMethodRules[index], `max_${key}`, max_rule);
                    } else if (ruleValue[`min_${key}`] == null) {
                        const min_rule = `max_value:${ruleValue[`max_${key}`] || Infinity}`;
                        Vue.set(this.rules.paymentMethodRules[index], `min_${key}`, min_rule);
                        const max_rule = `min_value:${ruleValue[`min_${key}`] || 0}|required`;
                        Vue.set(this.rules.paymentMethodRules[index], `max_${key}`, max_rule);
                    } else {
                        const min_rule = `max_value:${ruleValue[`max_${key}`] || Infinity}|required`;
                        Vue.set(this.rules.paymentMethodRules[index], `min_${key}`, min_rule);
                        const max_rule = `min_value:${ruleValue[`min_${key}`] || 0}`;
                        Vue.set(this.rules.paymentMethodRules[index], `max_${key}`, max_rule);
                    }
                    break;
            }
        },

        resetRules() {
            Vue.set(this.form, 'paymentMethodRules', []);
        },

        insertMemberTagOptions(tags) {
            Vue.set(this.options.memberTag, 'items', tags);
        },

        insertCustomerGroupOptions(customerGroups) {
            Vue.set(this.options.customerGroup, 'items', customerGroups);
        },

        addItemsPaginatedToState(data) {
            this.itemsPaginated.items = data.items;
            this.itemsPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.itemsPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.itemsPaginated.link = cloneDeep(data.config.link);
            this.itemsPaginated.showFilters = get(data.config, 'showFilters') || false;
            this.itemsPaginated.defaultSort = get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.itemsPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.itemsPaginated.activeColumns = activeColumns;
            }
        },

        updateForm({ path, value }) {
            set(this.form, path, value);
        },

        updateLoading(value) {
            this.loading = value;
        },

        defineFilterVisibility(value) {
            Vue.set(this.itemsPaginated, 'showFilters', value);
        },

        defineItemsPerPage(perPage) {
            this.itemsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.itemsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.itemsPaginated.filters, key);
            } else {
                Vue.set(this.itemsPaginated.filters, key, value);
            }
        },

        defineSorting({ key, value }) {
            Vue.set(this.itemsPaginated.variables, 'order', key);
            Vue.set(this.itemsPaginated.variables, 'direction', value);
        },

        insertPaymentMethod(paymentMethod) {
            if (paymentMethod.paymentMethodRules) {
                for (let i = 0; i < paymentMethod.paymentMethodRules.length; i++) {
                    this.rules.paymentMethodRules.push(paymentMethodRule);
                }
            }
            Vue.set(this, 'form', paymentMethod);
        },

        // Async actions
        createPaymentMethod(paymentMethod) {
            return PaymentMethodApi.create(paymentMethod).then((paymentMethod) => {
                this.insertPaymentMethod(cloneDeep(paymentMethod));
                return paymentMethod;
            });
        },

        updatePaymentMethod(paymentMethod) {
            return PaymentMethodApi.update(paymentMethod).then((paymentMethod) => {
                this.insertPaymentMethod(cloneDeep(paymentMethod));
                return paymentMethod;
            });
        },

        deletePaymentMethod(id) {
            return PaymentMethodApi.delete(parseInt(id));
        },

        // Partial patch used by the list's inline switch - does not touch `form`, since the list
        // and the edit form are independent and the list may be toggling a row that isn't loaded
        // into the form at all.
        updatePaymentMethodActive({ id, active }) {
            return PaymentMethodApi.update({ id, active });
        },

        fetchPaginatedItems(shopID, cmsID) {
            return PaymentMethodApi.fetchPaymentMethodsPaginated(this.itemsPaginated, shopID, cmsID).then(data => {
                this.addItemsPaginatedToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchPaymentMethod(id) {
            return PaymentMethodApi.fetchPaymentMethod(id).then(data => {
                this.insertPaymentMethod(cloneDeep(data));
            });
        },

        fetchTagOptions(cmsID) {
            return MemberTagApi.fetchList(cmsID)
                .then(tags => {
                    this.insertMemberTagOptions(tags);
                })
                .catch(err => {
                    throw err;
                });
        },

        fetchCustomerGroupOptions(cmsID) {
            return CustomerGroupApi.fetchList(cmsID)
                .then((customerGroups) => {
                    this.insertCustomerGroupOptions(customerGroups);
                })
                .catch((err) => {
                    throw err;
                });
        },
    }
});
